import { useQuery } from "@apollo/client";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { gql } from "generated-graphql";
import { type DiscrepanciesMatchingQuery } from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useEffect, useMemo, useState } from "react";

import { type ActionKey, computeActionEligibility } from "./DiscrepancyReviewDialog.helpers";
import { ResolveApplyToOthers } from "./ResolveApplyToOthers";

export type ResolveActionKey = ActionKey;

export interface ResolveActionOption {
  action: ResolveActionKey;
  label: string;
  /** Plain-language explanation of what the action does, shown under its radio. */
  description: string;
  /** The subset of targeted rows this action can apply to. */
  eligibleIds: string[];
  /** Bulleted reasons rows are skipped (from formatSkipSummary), when any are. */
  skipSummary?: string;
}

export const DISCREPANCIES_MATCHING_QUERY = gql(`
  query DiscrepanciesMatching($discrepancyId: ID!) {
    discrepanciesMatching(discrepancyId: $discrepancyId) {
      id
      processedManifestId
      manifestTrackingNumber
      facilityName
      wasteLineNumber
      status
      field
      vendorAlignmentSuspect
      vendorWasteLinesUnmatched
      systemFiledPending
      escalation
      eManifestValue
      vendorValue
      manifestDocumentValue
      documentDiscrepant
    }
  }
`);

/** One row of `discrepanciesMatching` — every row carrying the same finding, whether or not a given action can touch it. */
export type MatchingDiscrepancyRow = DiscrepanciesMatchingQuery["discrepanciesMatching"][number];

export interface ResolveDiscrepanciesDialogProps {
  open: boolean;
  /** How many rows the dialog is acting on — the selection size, or 1 for a row action. */
  totalCount: number;
  options: ResolveActionOption[];
  loading: boolean;
  /** The single targeted row's id — drives "apply to others". Only meaningful when totalCount is 1. */
  singleDiscrepancyId?: string;
  onClose: () => void;
  onConfirm: (action: ResolveActionKey, eligibleIds: string[], note: string | null) => void;
}

/**
 * The single resolution flow for discrepancy rows: pick an action (each
 * explained inline, with per-action eligibility for the targeted rows), add an
 * optional note, confirm. Opened from a section's "Resolve Selected" bulk
 * button or a row's resolve action; the caller decides which actions are
 * offered (Data Quality Issues omit "Correct Vendor Data").
 */
export function ResolveDiscrepanciesDialog({
  open,
  totalCount,
  options,
  loading,
  singleDiscrepancyId,
  onClose,
  onConfirm,
}: ResolveDiscrepanciesDialogProps) {
  const [selectedAction, setSelectedAction] = useState<ResolveActionKey | null>(null);
  const [note, setNote] = useState("");
  const [applyToOthers, setApplyToOthers] = useState(false);
  const { isStaff } = useCurrentUser();

  // Five selected rows have five different matching sets, so there's nothing
  // single to offer them — only a single-row target has one finding to match.
  const canApplyToOthers = isStaff && totalCount === 1 && singleDiscrepancyId != null;

  const { data: matchingData, loading: matchingLoading } = useQuery(DISCREPANCIES_MATCHING_QUERY, {
    variables: { discrepancyId: singleDiscrepancyId ?? "" },
    skip: !open || !canApplyToOthers,
  });
  const matchingRows = useMemo(
    () => matchingData?.discrepanciesMatching ?? [],
    [matchingData?.discrepanciesMatching]
  );
  const showApplyToOthers = canApplyToOthers && !matchingLoading && matchingRows.length > 0;

  // Fresh state per open — the dialog stays mounted between uses.
  useEffect(() => {
    if (!open) {
      setSelectedAction(null);
      setNote("");
      setApplyToOthers(false);
    }
  }, [open]);

  const selectedOption = options.find((o) => o.action === selectedAction);
  const rowWord = totalCount === 1 ? "row" : "rows";

  /*
   * The other rows this action would actually write, not every row that
   * matched. Eligibility is per action: a matching row the chosen action
   * cannot touch is skipped at confirm time, so counting all matches would
   * promise more than the button delivers. Recomputed as the action changes,
   * which also shows the reader that the number depends on it.
   *
   * Empty until an action is picked, so the checkbox does not claim a number
   * it cannot yet stand behind.
   */
  const otherEligible = useMemo(() => {
    if (!selectedOption || matchingRows.length === 0) return [];
    const eligible = new Set(
      computeActionEligibility(selectedOption.action, matchingRows).eligibleIds
    );
    return matchingRows.filter((r) => eligible.has(r.id));
  }, [selectedOption, matchingRows]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {totalCount === 1 ? "Resolve Discrepancy" : `Resolve ${totalCount} Selected Rows`}
      </DialogTitle>
      <DialogContent>
        <RadioGroup
          value={selectedAction ?? ""}
          onChange={(e) => {
            // The radio's value is always one of the rendered options' action,
            // so narrow against options rather than a separate list of every
            // ActionKey.
            const next = options.find((o) => o.action === e.target.value)?.action;
            if (next) setSelectedAction(next);
          }}
        >
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            {options.map((option) => {
              const eligibleCount = option.eligibleIds.length;
              const noneEligible = eligibleCount === 0;
              const partial = eligibleCount > 0 && eligibleCount < totalCount;
              return (
                <FormControlLabel
                  key={option.action}
                  value={option.action}
                  disabled={noneEligible || loading}
                  sx={{ alignItems: "flex-start", m: 0 }}
                  control={<Radio size="small" sx={{ mt: -0.5 }} />}
                  label={
                    <Stack spacing={0.25}>
                      <Typography variant="body2" fontWeight={600}>
                        {option.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.description}
                      </Typography>
                      {noneEligible && (
                        <Typography
                          variant="caption"
                          color="text.disabled"
                          sx={{ whiteSpace: "pre-line" }}
                        >
                          {`Not available for the selected ${rowWord}${
                            option.skipSummary ? `:\n${option.skipSummary}` : "."
                          }`}
                        </Typography>
                      )}
                      {partial && (
                        <Typography
                          variant="caption"
                          color="warning.main"
                          sx={{ whiteSpace: "pre-line" }}
                        >
                          {`Applies to ${eligibleCount} of ${totalCount} selected rows. Skipped:\n${
                            option.skipSummary ?? ""
                          }`}
                        </Typography>
                      )}
                    </Stack>
                  }
                />
              );
            })}
          </Stack>
        </RadioGroup>
        {showApplyToOthers && (
          <ResolveApplyToOthers
            matchingRows={matchingRows}
            otherEligible={otherEligible}
            actionPicked={selectedOption != null}
            checked={applyToOthers}
            disabled={loading}
            onChange={setApplyToOthers}
          />
        )}
        <TextField
          label="Note (optional)"
          multiline
          minRows={2}
          maxRows={4}
          fullWidth
          size="small"
          sx={{ mt: 2.5 }}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          helperText="Saved with the resolution, whichever action you choose."
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="staff"
          disabled={!selectedOption || selectedOption.eligibleIds.length === 0 || loading}
          onClick={() => {
            if (!selectedOption) return;
            // The matching rows' own eligibility, per the chosen action — a
            // matching row the action can't touch is skipped, not sent.
            const otherIds = applyToOthers ? otherEligible.map((r) => r.id) : [];
            onConfirm(
              selectedOption.action,
              [...selectedOption.eligibleIds, ...otherIds],
              note || null
            );
          }}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
}
